import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  FaChartLine, FaUsers, FaShoppingCart, FaDollarSign, FaArrowUp, FaArrowDown, 
  FaBoxOpen, FaGlobe, FaDesktop, FaMobile, FaTabletAlt, FaShoppingBag,
  FaPercent, FaClock, FaEye, FaMapMarkerAlt, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaSync, FaChartPie, FaChartBar
} from 'react-icons/fa';
import AdminNav from './AdminNav';
import { collection, getDocs, query, orderBy, limit, where, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { subscribeToActiveVisitors, cleanupStaleVisitors } from '../../firebase/analyticsService';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, Legend, PieChart, Pie, Cell, AreaChart, Area, ComposedChart
} from 'recharts';

const Container = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const DateRangeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DateButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.active ? '#C41E3A' : '#ddd'};
  background-color: ${props => props.active ? '#C41E3A' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: #C41E3A;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background-color: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #28a745;
  font-size: 0.9rem;
  
  .dot {
    width: 8px;
    height: 8px;
    background-color: #28a745;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.25rem;
  position: relative;
  overflow: hidden;
  
  ${props => props.highlight && `
    border-left: 4px solid ${props.highlightColor || '#C41E3A'};
  `}
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || '#e2f0ff'};
  color: ${props => props.color || '#0d6efd'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  margin-right: 0.75rem;
`;

const StatTitle = styled.h3`
  color: #666;
  font-size: 0.85rem;
  font-weight: 500;
  margin: 0;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StatChange = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: ${props => props.positive ? '#0f5132' : '#842029'};
  gap: 0.25rem;
`;

const StatSubtext = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.25rem;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.25rem;
  margin: 2rem 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChartContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ChartTitle = styled.h3`
  color: #333;
  margin: 0;
  font-size: 1.1rem;
`;

const ChartPlaceholder = styled.div`
  height: 300px;
  background-color: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  text-align: center;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  border-bottom: 1px solid #eee;
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const TableHeader = styled.th`
  padding: 0.75rem;
  text-align: left;
  color: #666;
  font-weight: 500;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  color: #333;
`;

const ProductImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 0.75rem;
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ProductName = styled.div`
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
`;

const EmptyTitle = styled.h2`
  color: #666;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

// Valid payment statuses that count as completed/paid orders
const PAID_STATUSES = ['paid', 'completed', 'shipped', 'delivered', 'processing'];
const UNPAID_STATUSES = ['awaiting_payment', 'pending', 'cancelled', 'refunded', 'failed'];

// Real-time Visitors Panel Component
const VisitorsPanel = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2c5282 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
  margin-bottom: 2rem;
`;

const VisitorsPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const VisitorsPanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VisitorCount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const VisitorsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const VisitorCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.85rem;
`;

const VisitorLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const VisitorPage = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    if (props.status === 'paid' || props.status === 'completed' || props.status === 'delivered') return '#d1e7dd';
    if (props.status === 'shipped' || props.status === 'processing') return '#fff3cd';
    if (props.status === 'cancelled' || props.status === 'refunded' || props.status === 'failed') return '#f8d7da';
    return '#e2e3e5';
  }};
  color: ${props => {
    if (props.status === 'paid' || props.status === 'completed' || props.status === 'delivered') return '#0f5132';
    if (props.status === 'shipped' || props.status === 'processing') return '#856404';
    if (props.status === 'cancelled' || props.status === 'refunded' || props.status === 'failed') return '#842029';
    return '#41464b';
  }};
`;

const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Core stats
  const [stats, setStats] = useState({
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    totalOrders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    cancelledOrders: 0,
    customers: 0,
    avgOrderValue: 0,
    conversionRate: 0,
    abandonedCarts: 0,
    revenueChange: 0,
    ordersChange: 0,
    customersChange: 0,
    avgOrderValueChange: 0
  });
  
  // Real-time visitors
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [visitorsByState, setVisitorsByState] = useState({});
  
  // Chart data
  const [salesData, setSalesData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [trafficSourceData, setTrafficSourceData] = useState([]);
  
  // Fetch data on mount and when date range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);
  
  // Subscribe to real-time visitors
  useEffect(() => {
    let unsubscribe;
    
    try {
      unsubscribe = subscribeToActiveVisitors((visitors) => {
        setActiveVisitors(visitors);
        
        // Group by state
        const byState = {};
        visitors.forEach(v => {
          const state = v.location?.regionCode || v.location?.region || 'Unknown';
          byState[state] = (byState[state] || 0) + 1;
        });
        setVisitorsByState(byState);
      });
    } catch (error) {
      console.error('Error subscribing to visitors:', error);
    }
    
    // Cleanup stale visitors periodically
    const cleanupInterval = setInterval(() => {
      cleanupStaleVisitors();
    }, 60000); // Every minute
    
    return () => {
      if (unsubscribe) unsubscribe();
      clearInterval(cleanupInterval);
    };
  }, []);
  
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Calculate date ranges based on selection
      const now = new Date();
      let startDate, previousStartDate, previousEndDate;
      
      switch (dateRange) {
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          previousStartDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          previousEndDate = startDate;
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          previousStartDate = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
          previousEndDate = startDate;
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          previousStartDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
          previousEndDate = startDate;
          break;
        case 'all':
        default:
          startDate = new Date(2020, 0, 1);
          previousStartDate = null;
          previousEndDate = null;
      }
      
      // Get all orders
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'));
      const ordersSnapshot = await getDocs(ordersQuery);
      
      // Initialize tracking variables
      const allOrders = [];
      const customers = new Set();
      let paidRevenue = 0;
      let pendingRevenue = 0;
      let paidOrders = 0;
      let pendingOrders = 0;
      let cancelledOrders = 0;
      let abandonedCarts = 0;
      const productSales = new Map();
      const dailySales = new Map();
      const statusCounts = {};
      const hourlyCounts = Array(24).fill(0);
      
      // Previous period tracking
      let prevPaidRevenue = 0;
      let prevPaidOrders = 0;
      const prevCustomers = new Set();
      
      // Current period tracking
      let currPaidRevenue = 0;
      let currPaidOrders = 0;
      const currCustomers = new Set();
      
      ordersSnapshot.forEach(doc => {
        const orderData = doc.data();
        const orderId = doc.id;
        const createdAt = orderData.createdAt?.toDate ? orderData.createdAt.toDate() : new Date(orderData.createdAt);
        const orderTotal = orderData.orderDetails?.total || orderData.total || 0;
        const customerEmail = orderData.customerInfo?.email;
        const items = orderData.orderDetails?.items || orderData.items || [];
        const status = orderData.status || 'pending';
        const paymentStatus = orderData.paymentStatus || 'unpaid';
        
        // Determine if this is a paid order
        const isPaid = paymentStatus === 'paid' || PAID_STATUSES.includes(status.toLowerCase());
        const isCancelled = status === 'cancelled' || status === 'refunded' || status === 'failed';
        const isAbandoned = status === 'awaiting_payment' && paymentStatus === 'unpaid';
        
        // Add to orders array
        allOrders.push({
          id: orderId,
          customer: orderData.customerInfo?.name || 'Guest',
          email: customerEmail,
          date: createdAt,
          total: orderTotal,
          status: status,
          paymentStatus: paymentStatus,
          isPaid: isPaid,
          items: items.length
        });
        
        // Track status counts
        statusCounts[status] = (statusCounts[status] || 0) + 1;
        
        // **FIXED: Only count revenue from PAID orders**
        if (isPaid) {
          paidRevenue += orderTotal;
          paidOrders++;
          
          // Process items for product sales (only from paid orders)
          items.forEach(item => {
            const productId = item.id;
            const productName = item.name;
            const quantity = item.quantity || 1;
            const price = item.price || 0;
            
            if (productId && productName) {
              if (productSales.has(productId)) {
                const product = productSales.get(productId);
                product.quantity += quantity;
                product.revenue += price * quantity;
                productSales.set(productId, product);
              } else {
                productSales.set(productId, {
                  id: productId,
                  name: productName,
                  quantity: quantity,
                  revenue: price * quantity,
                  image: item.imageUrl || item.image || ''
                });
              }
            }
          });
          
          // Track daily sales (only paid orders)
          const dateKey = createdAt.toISOString().split('T')[0];
          dailySales.set(dateKey, (dailySales.get(dateKey) || 0) + orderTotal);
          
          // Track hourly distribution
          hourlyCounts[createdAt.getHours()]++;
        } else if (isCancelled) {
          cancelledOrders++;
        } else if (isAbandoned) {
          abandonedCarts++;
          pendingRevenue += orderTotal;
          pendingOrders++;
        } else {
          pendingRevenue += orderTotal;
          pendingOrders++;
        }
        
        // Add to unique customers
        if (customerEmail) {
          customers.add(customerEmail);
        }
        
        // Period comparison (only for paid orders)
        if (isPaid && previousStartDate) {
          if (createdAt >= startDate) {
            currPaidRevenue += orderTotal;
            currPaidOrders++;
            if (customerEmail) currCustomers.add(customerEmail);
          } else if (createdAt >= previousStartDate && createdAt < previousEndDate) {
            prevPaidRevenue += orderTotal;
            prevPaidOrders++;
            if (customerEmail) prevCustomers.add(customerEmail);
          }
        }
      });
      
      // Calculate changes
      const revenueChange = prevPaidRevenue === 0 ? (currPaidRevenue > 0 ? 100 : 0)
        : ((currPaidRevenue - prevPaidRevenue) / prevPaidRevenue) * 100;
      
      const ordersChange = prevPaidOrders === 0 ? (currPaidOrders > 0 ? 100 : 0)
        : ((currPaidOrders - prevPaidOrders) / prevPaidOrders) * 100;
      
      const customersChange = prevCustomers.size === 0 ? (currCustomers.size > 0 ? 100 : 0)
        : ((currCustomers.size - prevCustomers.size) / prevCustomers.size) * 100;
      
      const avgOrderValue = paidOrders === 0 ? 0 : paidRevenue / paidOrders;
      const prevAvgOrderValue = prevPaidOrders === 0 ? 0 : prevPaidRevenue / prevPaidOrders;
      const currAvgOrderValue = currPaidOrders === 0 ? 0 : currPaidRevenue / currPaidOrders;
      const avgOrderValueChange = prevAvgOrderValue === 0 ? (currAvgOrderValue > 0 ? 100 : 0)
        : ((currAvgOrderValue - prevAvgOrderValue) / prevAvgOrderValue) * 100;
      
      // Calculate conversion rate (paid orders / total orders)
      const conversionRate = allOrders.length === 0 ? 0 : (paidOrders / allOrders.length) * 100;
      
      // Set stats
      setStats({
        totalRevenue: paidRevenue + pendingRevenue,
        paidRevenue,
        pendingRevenue,
        totalOrders: allOrders.length,
        paidOrders,
        pendingOrders,
        cancelledOrders,
        customers: customers.size,
        avgOrderValue,
        conversionRate,
        abandonedCarts,
        revenueChange,
        ordersChange,
        customersChange,
        avgOrderValueChange
      });
      
      // Prepare order status chart data
      setOrderStatusData([
        { name: 'Completed', value: paidOrders, color: '#28a745' },
        { name: 'Pending', value: pendingOrders, color: '#ffc107' },
        { name: 'Abandoned', value: abandonedCarts, color: '#dc3545' },
        { name: 'Cancelled', value: cancelledOrders, color: '#6c757d' }
      ].filter(d => d.value > 0));
      
      // Prepare sales chart data
      const salesChartData = [];
      const dayCount = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 180;
      
      for (let i = dayCount - 1; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateKey = date.toISOString().split('T')[0];
        const label = dateRange === '7d' 
          ? date.toLocaleDateString('en-US', { weekday: 'short' })
          : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        
        salesChartData.push({
          name: label,
          revenue: dailySales.get(dateKey) || 0,
          date: dateKey
        });
      }
      
      // Aggregate if too many data points
      if (salesChartData.length > 30) {
        const aggregated = [];
        const chunkSize = Math.ceil(salesChartData.length / 30);
        for (let i = 0; i < salesChartData.length; i += chunkSize) {
          const chunk = salesChartData.slice(i, i + chunkSize);
          const totalRevenue = chunk.reduce((sum, d) => sum + d.revenue, 0);
          aggregated.push({
            name: chunk[0].name,
            revenue: totalRevenue
          });
        }
        setSalesData(aggregated);
      } else {
        setSalesData(salesChartData);
      }
      
      // Set top products (from paid orders only)
      const topProductsList = Array.from(productSales.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
      setTopProducts(topProductsList);
      
      // Set recent orders
      setRecentOrders(allOrders.slice(0, 10));
      
      // Set hourly data
      const hourlyChartData = hourlyCounts.map((count, hour) => ({
        hour: `${hour.toString().padStart(2, '0')}:00`,
        orders: count
      }));
      setHourlyData(hourlyChartData);
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Format percentage
  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };
  
  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalyticsData();
  };
  
  // Chart colors
  const COLORS = ['#28a745', '#ffc107', '#dc3545', '#6c757d', '#0d6efd'];
  
  return (
    <Container>
      <AdminNav />
      
      <Header>
        <HeaderLeft>
          <Title>Analytics Dashboard</Title>
          <Subtitle>Track sales, revenue, and customer insights</Subtitle>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <LiveIndicator>
            <span className="dot"></span>
            Live Data
          </LiveIndicator>
          <DateRangeSelector>
            <DateButton active={dateRange === '7d'} onClick={() => setDateRange('7d')}>7 Days</DateButton>
            <DateButton active={dateRange === '30d'} onClick={() => setDateRange('30d')}>30 Days</DateButton>
            <DateButton active={dateRange === '90d'} onClick={() => setDateRange('90d')}>90 Days</DateButton>
            <DateButton active={dateRange === 'all'} onClick={() => setDateRange('all')}>All Time</DateButton>
          </DateRangeSelector>
          <RefreshButton onClick={handleRefresh} disabled={refreshing}>
            <FaSync className={refreshing ? 'fa-spin' : ''} />
            Refresh
          </RefreshButton>
        </div>
      </Header>
      
      {/* Real-time Visitors Panel */}
      <VisitorsPanel>
        <VisitorsPanelHeader>
          <VisitorsPanelTitle>
            <FaGlobe /> Real-Time Visitors
          </VisitorsPanelTitle>
          <LiveIndicator style={{ color: 'white' }}>
            <span className="dot" style={{ backgroundColor: '#4ade80' }}></span>
            Live
          </LiveIndicator>
        </VisitorsPanelHeader>
        <VisitorCount>{activeVisitors.length}</VisitorCount>
        <div style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: '1rem' }}>
          Active users on the site right now
        </div>
        
        {activeVisitors.length > 0 ? (
          <>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}>
              <strong>Visitors by State:</strong>
            </div>
            <VisitorsList>
              {Object.entries(visitorsByState)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([state, count]) => (
                  <VisitorCard key={state}>
                    <VisitorLocation>
                      <FaMapMarkerAlt />
                      {state}
                    </VisitorLocation>
                    <div>{count} visitor{count !== 1 ? 's' : ''}</div>
                  </VisitorCard>
                ))}
            </VisitorsList>
          </>
        ) : (
          <div style={{ opacity: 0.7, fontSize: '0.9rem' }}>
            No active visitors at the moment
          </div>
        )}
      </VisitorsPanel>
      
      {loading ? (
        <EmptyState>
          <EmptyTitle>Loading analytics data...</EmptyTitle>
        </EmptyState>
      ) : (
        <>
          {/* Key Metrics */}
          <SectionTitle><FaChartBar /> Key Metrics (Paid Orders Only)</SectionTitle>
          <StatsGrid>
            <StatCard highlight highlightColor="#28a745">
              <StatHeader>
                <StatIcon bgColor="#d1e7dd" color="#0f5132">
                  <FaDollarSign />
                </StatIcon>
                <StatTitle>Paid Revenue</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(stats.paidRevenue)}</StatValue>
              <StatChange positive={stats.revenueChange >= 0}>
                {stats.revenueChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                {formatPercentage(stats.revenueChange)} vs previous period
              </StatChange>
              <StatSubtext>Only from completed orders</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon bgColor="#fff3cd" color="#856404">
                  <FaExclamationTriangle />
                </StatIcon>
                <StatTitle>Pending Revenue</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(stats.pendingRevenue)}</StatValue>
              <StatSubtext>{stats.pendingOrders} orders awaiting payment</StatSubtext>
            </StatCard>
            
            <StatCard highlight highlightColor="#28a745">
              <StatHeader>
                <StatIcon bgColor="#d1e7dd" color="#0f5132">
                  <FaCheckCircle />
                </StatIcon>
                <StatTitle>Completed Orders</StatTitle>
              </StatHeader>
              <StatValue>{stats.paidOrders}</StatValue>
              <StatChange positive={stats.ordersChange >= 0}>
                {stats.ordersChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                {formatPercentage(stats.ordersChange)} vs previous period
              </StatChange>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon bgColor="#cfe2ff" color="#0d6efd">
                  <FaUsers />
                </StatIcon>
                <StatTitle>Total Customers</StatTitle>
              </StatHeader>
              <StatValue>{stats.customers}</StatValue>
              <StatChange positive={stats.customersChange >= 0}>
                {stats.customersChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                {formatPercentage(stats.customersChange)} vs previous period
              </StatChange>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon bgColor="#e2e3e5" color="#6c757d">
                  <FaShoppingBag />
                </StatIcon>
                <StatTitle>Avg. Order Value</StatTitle>
              </StatHeader>
              <StatValue>{formatCurrency(stats.avgOrderValue)}</StatValue>
              <StatChange positive={stats.avgOrderValueChange >= 0}>
                {stats.avgOrderValueChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                {formatPercentage(stats.avgOrderValueChange)} vs previous period
              </StatChange>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon bgColor="#d1e7dd" color="#0f5132">
                  <FaPercent />
                </StatIcon>
                <StatTitle>Conversion Rate</StatTitle>
              </StatHeader>
              <StatValue>{stats.conversionRate.toFixed(1)}%</StatValue>
              <StatSubtext>Orders completed vs total orders</StatSubtext>
            </StatCard>
            
            <StatCard highlight highlightColor="#dc3545">
              <StatHeader>
                <StatIcon bgColor="#f8d7da" color="#842029">
                  <FaTimesCircle />
                </StatIcon>
                <StatTitle>Abandoned Carts</StatTitle>
              </StatHeader>
              <StatValue>{stats.abandonedCarts}</StatValue>
              <StatSubtext>Orders awaiting payment</StatSubtext>
            </StatCard>
            
            <StatCard>
              <StatHeader>
                <StatIcon bgColor="#e2e3e5" color="#6c757d">
                  <FaShoppingCart />
                </StatIcon>
                <StatTitle>Total Orders</StatTitle>
              </StatHeader>
              <StatValue>{stats.totalOrders}</StatValue>
              <StatSubtext>All orders (paid + unpaid)</StatSubtext>
            </StatCard>
          </StatsGrid>
          
          {/* Revenue Chart */}
          <SectionTitle><FaChartLine /> Revenue Trend (Paid Orders Only)</SectionTitle>
          <ChartContainer>
            <ChartHeader>
              <ChartTitle>Daily Revenue</ChartTitle>
            </ChartHeader>
            
            {salesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={salesData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C41E3A" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#C41E3A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#C41E3A" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartPlaceholder>
                No paid orders in the selected period
              </ChartPlaceholder>
            )}
          </ChartContainer>
          
          {/* Order Status and Hourly Distribution */}
          <TwoColumnGrid>
            <ChartContainer>
              <ChartHeader>
                <ChartTitle>Order Status Distribution</ChartTitle>
              </ChartHeader>
              
              {orderStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ChartPlaceholder>
                  No order data available
                </ChartPlaceholder>
              )}
            </ChartContainer>
            
            <ChartContainer>
              <ChartHeader>
                <ChartTitle>Orders by Hour of Day</ChartTitle>
              </ChartHeader>
              
              {hourlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#0d6efd" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ChartPlaceholder>
                  No hourly data available
                </ChartPlaceholder>
              )}
            </ChartContainer>
          </TwoColumnGrid>
          
          {/* Top Products and Recent Orders */}
          <TwoColumnGrid>
            <ChartContainer>
              <ChartHeader>
                <ChartTitle>Top Selling Products (Paid Orders)</ChartTitle>
              </ChartHeader>
              
              {topProducts.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Product</TableHeader>
                      <TableHeader>Qty Sold</TableHeader>
                      <TableHeader>Revenue</TableHeader>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {topProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <ProductInfo>
                            {product.image && (
                              <ProductImage src={product.image} alt={product.name} />
                            )}
                            <ProductName>{product.name}</ProductName>
                          </ProductInfo>
                        </TableCell>
                        <TableCell>{product.quantity}</TableCell>
                        <TableCell>{formatCurrency(product.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <ChartPlaceholder>
                  No product sales data from paid orders
                </ChartPlaceholder>
              )}
            </ChartContainer>
            
            <ChartContainer>
              <ChartHeader>
                <ChartTitle>Recent Orders</ChartTitle>
              </ChartHeader>
              
              {recentOrders.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Order ID</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Amount</TableHeader>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {recentOrders.map(order => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <div style={{ fontWeight: 500 }}>{order.id.substring(0, 8)}...</div>
                          <div style={{ fontSize: '0.75rem', color: '#999' }}>{formatDate(order.date)}</div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.isPaid ? 'paid' : order.status}>
                            {order.isPaid ? 'Paid' : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <div style={{ fontWeight: order.isPaid ? 600 : 400, color: order.isPaid ? '#0f5132' : '#666' }}>
                            {formatCurrency(order.total)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <ChartPlaceholder>
                  No recent orders
                </ChartPlaceholder>
              )}
            </ChartContainer>
          </TwoColumnGrid>
        </>
      )}
    </Container>
  );
};

export default AnalyticsDashboard;
